import type { BlogPost } from '../types';

/**
 * "The Price of Standing Still" — Ayesshah Alikhan's October 2026 whitepaper.
 *
 * Copy is verbatim from docs/The_Price_of_Standing_Still_WisdomX_Orbis.pdf
 * (6 pages). Figure 1 is reproduced as a table and Figure 2 as a `flow` block.
 * The original PDF is served unchanged from
 * landing/public/whitepapers/the-price-of-standing-still.pdf.
 */
export const thePriceOfStandingStill: BlogPost = {
  slug: 'the-price-of-standing-still',
  title: 'The Price of Standing Still: What Your Legacy Estate Really Costs',
  description:
    'The five hidden costs of legacy systems that never appear on an IT invoice — and how to turn them into a prioritised, evidence-based decommissioning plan.',
  dek: 'What your legacy estate really costs — and how to turn it into a decision you can act on.',
  category: 'Whitepaper',
  publishedAt: '2026-10-06',
  author: {
    name: 'Ayesshah Alikhan',
    role: 'Founder & CEO, WisdomX – Brain On Demand Ltd',
    bio: "Ayesshah Alikhan is Founder and CEO of WisdomX – Brain On Demand Ltd, and has more than 25 years' experience leading enterprise transformation, legacy estate rationalisation and change across financial services, central government and healthcare.",
  },
  ogImage: '/og/the-price-of-standing-still.png',
  pdf: {
    href: '/whitepapers/the-price-of-standing-still.pdf',
    label: 'Download the whitepaper (PDF)',
  },
  highlights: [
    'The five hidden costs that never appear on an IT invoice',
    "Why estates don't shrink: the decision gap, not the technology",
    'How WisdomX Orbis turns hidden cost into a prioritised, evidence-based plan',
    'How to become a WisdomX Orbis pilot partner',
  ],
  blocks: [
    // ── Executive summary ────────────────────────────────────────────────
    { kind: 'h2', eyebrow: 'Executive summary', text: 'A decision someone hasn’t made yet' },
    {
      kind: 'p',
      lead: true,
      text: "Every legacy system that stays switched on is a decision someone hasn't made yet. The licence renewal is the only part of that decision that ever shows up on an invoice.",
    },
    {
      kind: 'p',
      text: 'Walk into any enterprise architecture review and you will hear the same three sentences:',
    },
    {
      kind: 'voices',
      items: [
        "We can't turn it off; finance still runs month-end through it.",
        "Nobody's quite sure what depends on it.",
        "Let's look at it again next year.",
      ],
    },
    {
      kind: 'p',
      text: 'None of these are unreasonable. Each one protects the business from a visible risk: an outage, a failed audit, an angry CFO. But together they create a far larger, invisible one. The estate keeps growing, the run budget keeps rising, and the organisation quietly pays for its own indecision every single month.',
    },
    {
      kind: 'p',
      text: 'This paper is about that bill — what is really on it, why most organisations never see it in full, and how to turn it into a decision you can act on.',
    },

    { kind: 'h3', text: 'The bill you see, and the bill you pay' },
    {
      kind: 'p',
      text: 'Ask a CIO what a legacy application costs and you will usually get a tidy answer: licences, support contracts, hosting, and a slice of the infrastructure team. That number is real. It is also the smallest part of the story.',
    },
    {
      kind: 'stats',
      items: [
        {
          value: '60–80%',
          label:
            'Share of enterprise IT budgets commonly reported as spent simply keeping existing systems running*',
        },
        {
          value: '$4.44M',
          label: 'Global average cost of a data breach (IBM, Cost of a Data Breach Report 2025)',
        },
        { value: '5', label: 'Hidden costs that rarely appear on any single budget line' },
      ],
      note: '* Approximate, widely reported industry range.',
    },
    {
      kind: 'p',
      text: 'Analyst estimates vary, but most put the share of enterprise IT budgets spent simply keeping existing systems running somewhere between 60% and 80%. That leaves a minority of every pound for the change the board asked for. The direct run-cost is only the visible tip; underneath it sit at least five costs that rarely appear on any single budget line.',
    },
    {
      kind: 'table',
      caption: 'Figure 1. The five hidden costs of a legacy estate',
      head: ['Cost', 'Where it hides', 'Who usually feels it'],
      rows: [
        [
          'People and productivity',
          'Workarounds, re-keying, slow screens',
          'Operations, finance, service desk',
        ],
        ['Scarce skills', 'Premium contractors, key-person dependency', 'IT leadership, HR'],
        [
          'Security and compliance',
          'Unpatched platforms, audit findings',
          'CISO, risk, the regulator',
        ],
        [
          'Carbon and energy',
          'Under-used servers, duplicate storage',
          'Sustainability, facilities',
        ],
        ['Opportunity', 'Delayed programmes, stalled AI adoption', 'The board, customers'],
      ],
    },

    // ── The hidden bill ──────────────────────────────────────────────────
    {
      kind: 'h2',
      eyebrow: 'The hidden bill',
      text: 'Five costs that never reach the invoice',
      id: 'hidden-costs',
    },

    { kind: 'h3', number: '01', text: 'The people tax' },
    {
      kind: 'p',
      text: 'Legacy systems are paid for twice: once by IT, and again by everyone who must use them. The second payment never appears on an IT budget.',
    },
    {
      kind: 'p',
      text: 'It shows up as the claims handler who copies data between three screens. The finance analyst who exports to Excel because the reporting module stopped being supported in 2014. The developer who spends a week testing a one-line change because nobody trusts the regression suite.',
    },
    {
      kind: 'p',
      text: 'Multiply a few lost hours a week across a few hundred staff and the number quickly dwarfs the licence fee. Worse, it compounds. The more workarounds people build, the harder the system becomes to retire — because now the workarounds are business-critical too.',
    },

    { kind: 'h3', number: '02', text: 'The knowledge cliff' },
    {
      kind: 'p',
      text: 'Every long-lived estate has a handful of people who “just know” how things work. They know which batch job must run before the interface fires, and which table nobody should touch. Their knowledge is rarely documented; they are often near retirement and increasingly expensive to replace.',
    },
    {
      kind: 'p',
      text: "When those people leave, the organisation doesn't just lose capacity. It loses the confidence to change anything at all. Specialist contractors fill the gap at a premium, and the system becomes harder to decommission precisely because fewer people understand it.",
    },
    {
      kind: 'quote',
      text: 'The cruellest irony of legacy estates: the longer you wait to retire a system, the more expensive it becomes to retire safely.',
    },

    { kind: 'h3', number: '03', text: 'The attack surface nobody owns' },
    {
      kind: 'p',
      text: 'Every unnecessary or poorly maintained application expands the potential attack surface. Unsupported legacy platforms and middleware can contain vulnerabilities for which security updates are no longer available, while data retained beyond its required retention period can create additional exposure.',
    },
    {
      kind: 'p',
      text: "These risks are financially material: IBM's 2025 Cost of a Data Breach Report estimates the global average cost of a breach at approximately $4.44 million. Regulators, particularly in financial services, increasingly expect firms to understand, map and manage the technology dependencies and vulnerabilities underpinning important business services.",
    },
    {
      kind: 'p',
      text: 'The practical challenge is that vulnerability, architecture and application-portfolio information is often fragmented across different teams and systems, making it difficult to identify which assets present the greatest risk.',
    },

    { kind: 'h3', number: '04', text: 'The carbon line on the balance sheet' },
    {
      kind: 'p',
      text: "An idle legacy server still draws power, still needs cooling, and still occupies rack space or cloud capacity. Across hundreds of under-used applications, that adds up to a measurable share of an organisation's digital footprint.",
    },
    {
      kind: 'p',
      text: 'As sustainability reporting tightens, this is no longer a nice-to-have. Retiring redundant systems is one of the few sustainability actions that saves money at the same time as it cuts emissions. Yet it is almost never quantified in the business case — which means it is almost never used to win the argument.',
    },

    { kind: 'h3', number: '05', text: "The future you can't fund" },
    {
      kind: 'p',
      text: 'The largest cost of all is the one that never gets measured: what the organisation could have done instead.',
    },
    {
      kind: 'p',
      text: 'Every boardroom is now asking about AI. But AI runs on clean, connected, well-governed data — and legacy estates are where data goes to fragment. Duplicate customer records across five systems, undocumented interfaces and unclear ownership make it very hard to deploy AI safely, let alone at scale.',
    },
    {
      kind: 'quote',
      text: 'The legacy bill is not just money spent. It is ambition deferred. Every year an estate stays bloated is a year of transformation the business pays for but never receives.',
    },

    // ── The decision gap ─────────────────────────────────────────────────
    {
      kind: 'h2',
      eyebrow: 'The decision gap',
      text: "Why estates don't shrink: it's the decision, not the technology",
      id: 'decision-gap',
    },
    {
      kind: 'p',
      text: 'Most organisations already know they have too many systems. They often have an application inventory, a CMDB, a vulnerability scanner and an archival option. What they lack is the bridge between knowing and acting.',
    },
    {
      kind: 'p',
      text: [
        {
          k: 'text',
          v: 'Portfolio tools show what you have. Archival tools handle what happens after you decide. In between sits the hardest question in enterprise IT: ',
        },
        {
          k: 'strong',
          v: "which systems should we retire, in what order, and how do we prove it's safe?",
        },
      ],
    },
    {
      kind: 'flow',
      caption:
        'Figure 2. Where WisdomX Orbis sits: the execution intelligence layer between visibility and archival',
      stages: [
        { title: 'Portfolio visibility', body: 'What you have' },
        {
          title: 'The decision gap',
          body: "Which systems to retire, in what order — and how to prove it's safe",
          highlight: true,
        },
        { title: 'Archival & retirement', body: 'What happens after you decide' },
      ],
    },
    {
      kind: 'p',
      text: "Answering it today usually means months of spreadsheets, workshops and stakeholder interviews. By the time the analysis is done, the data is stale and the sponsor has moved on. Having led transformation and decommissioning programmes for more than 25 years, I've watched this pattern repeat across financial services, government and healthcare.",
    },
    {
      kind: 'quote',
      text: 'The technology to retire systems exists. The confidence to decide does not.',
      cite: 'Ayesshah Alikhan, Founder & CEO, WisdomX',
    },

    // ── The solution ─────────────────────────────────────────────────────
    {
      kind: 'h2',
      eyebrow: 'The solution',
      text: 'From hidden cost to clear decision: WisdomX Orbis',
      id: 'solution',
    },
    {
      kind: 'p',
      text: 'WisdomX Orbis is a Decision Intelligence platform built for exactly this gap. It is the execution intelligence layer between portfolio visibility and post-decision archival — AI-operated and human-led.',
    },
    {
      kind: 'p',
      text: 'Orbis brings four capabilities together in one place. Instead of another dashboard, it produces a single, prioritised, evidence-based list of decommissioning candidates — showing which systems cost the most, carry the most risk, and are safest to retire first.',
    },
    {
      kind: 'cards',
      items: [
        { title: 'Decommission', body: 'Prioritised, evidence-based retirement candidates' },
        { title: 'Vulnerability Management', body: 'Risk exposure scored against every asset' },
        { title: 'Threat Intelligence', body: 'Context on the threats that matter to your estate' },
        { title: 'AI SOC', body: 'Continuous, AI-assisted security operations' },
      ],
    },

    // ── How Orbis helps ──────────────────────────────────────────────────
    {
      kind: 'h2',
      eyebrow: 'How Orbis helps',
      text: 'Built for the decision, not just the dashboard',
      id: 'how-orbis-helps',
    },
    {
      kind: 'steps',
      items: [
        {
          title: 'It works with what you already have.',
          body: 'Orbis is agentless. It analyses data already held in your ITSM, CMDB, ERP and discovery platforms — including a tested ServiceNow connector — so there is nothing to install on your network.',
        },
        {
          title: 'It puts the hidden costs on the table.',
          body: 'Run-cost, risk exposure, dependencies, and carbon and energy savings are scored side by side, so the business case is built on evidence rather than opinion.',
        },
        {
          title: 'It explains its reasoning.',
          body: 'Every recommendation comes with the rationale behind it, so architects, risk owners and finance can challenge it, refine it and sign it off.',
        },
        {
          title: 'It connects decision to action.',
          body: 'Through our exclusive archival partnership with Snapshot, the data from retired systems is preserved in a secure, compliant, audit-ready form — so “we might need the data” no longer keeps a system alive.',
        },
      ],
    },
    {
      kind: 'quote',
      text: 'The result is a decommissioning programme that moves at the speed of evidence, not the speed of committee.',
    },
  ],
  cta: {
    eyebrow: 'Become a pilot partner',
    title: "If your organisation is paying the hidden bill, let's put a number on it.",
    body: 'WisdomX Orbis is published on Microsoft Marketplace, and we are now welcoming a select group of pilot partners — mid-sized organisations in insurance, financial services and managed services with a live legacy estate to rationalise.',
  },
  sources:
    'Sources: IBM, Cost of a Data Breach Report 2025. IT run-cost share is an approximate, widely reported industry range. WisdomX, WisdomX Orbis and related names are trademarks of WisdomX – Brain On Demand Ltd. Other product and company names are the property of their respective owners.',
};
